import { type FC, type SyntheticEvent } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { type CSSObject } from "styled-components";

import { TAB_STATUSES } from "@/constants";
import { useTabStatus } from "@/hooks/useTabStatus";
import { useThunkDispatch } from "@/store/hooks";
import { currentOrganizationSelector } from "@/store/v2/currentOrganization/currentOrganizationSlice";
import { isPublicPage } from "@/utils/visualBuilder";

import { handleTab } from "./GlobalTabs/GlobalTabsDataSlice";
import ShareOptions from "./Share/components/ShareOptions";
import * as S from "./styled";
import { setSimpleValue, setVisualBuilderValues } from "../../store/actions";

const customStyles: Record<string, CSSObject> = {
  customFabStyle: {
    backgroundColor: "#505050",
    boxShadow: "none",
    color: "#fff",
    height: "100%",
    width: "100%",
    "&:hover": {
      background: "transparent",
      boxShadow: "none",
      outline: "none",
    },
    "&:active": {
      background: "transparent",
      boxShadow: "none",
    },
    "&:focus": {
      background: "transparent",
      boxShadow: "none",
    },
    "& .MuiTouchRipple-root": {
      display: "none !important",
    },
    "& p": {
      textTransform: "capitalize",
    },
  },
  shareContainer: {
    alignItems: "center",
    display: "flex",
    height: "100%",
    justifyContent: "center",
    width: "100%",
    "& p": {
      paddingLeft: 0,
    },
  },
  shareButtonsContainer: {
    display: "grid",
    gap: "8px",
    position: "absolute",
    top: "120%",
    "& svg": {
      backgroundColor: "none",
    },
  },
};

const tabValues = [TAB_STATUSES.COLLECT, TAB_STATUSES.CUSTOMIZE, TAB_STATUSES.SHARE];

interface ExperienceTabsProps {
  adjustedTabStatus: number;
  isBelowSmallWidth: boolean;
  experienceId: string;
  experienceMediaItemsLength: number;
  propertyId: string;
  tabStatus: string;
  userId: string;
}

const ExperienceTabs: FC<ExperienceTabsProps> = ({
  adjustedTabStatus,
  isBelowSmallWidth,
  experienceId,
  experienceMediaItemsLength,
  propertyId,
  tabStatus,
  userId,
}) => {
  const navigate = useNavigate();

  const dispatch = useThunkDispatch();

  const { isTabShare, isTabCollect } = useTabStatus();

  const isShareDisabled = experienceMediaItemsLength < 1;

  const { data: organization } = useSelector(currentOrganizationSelector);
  const organizationId = organization?.id;
  const isOrganizationsSection = location.pathname.startsWith("/organizations");
  const tabPrefix = isOrganizationsSection ? `/organizations/${organizationId}` : "";

  const navigateToNewTab = (tabId: string) => {
    const newState = {
      status: "active",
      name: "Loading",
    };

    dispatch(handleTab({ userId, tabId, newState }));
    navigate(tabId);
  };

  const handleTabChange = (newTabStatus: TAB_STATUSES) => {
    if (newTabStatus === TAB_STATUSES.SHARE) {
      if (isTabCollect) {
        navigateToNewTab(`${tabPrefix}/properties/${propertyId}/experiences/${experienceId}/customize`);
      }
      return;
    }

    navigateToNewTab(`${tabPrefix}/properties/${propertyId}/experiences/${experienceId}/${newTabStatus}`);
  };

  const handleChange = async (_e: SyntheticEvent, value: number) => {
    if (experienceMediaItemsLength >= 1) {
      const newTabStatus = tabValues[value];
      dispatch(setVisualBuilderValues(propertyId, "prevTabStatus", tabStatus));
      handleTabChange(newTabStatus);
    } else {
      handleTabChange(TAB_STATUSES.COLLECT);
    }
  };

  const handleShareButtonClick = async () => {
    if (!isTabShare) {
      dispatch(setVisualBuilderValues(propertyId, "prevTabStatus", tabStatus));
      handleTabChange(TAB_STATUSES.SHARE);
    }
  };

  const openPreview = () => {
    if (experienceMediaItemsLength < 1) {
      return;
    }

    const urlObject = new URL(window.location.href);
    const { origin, pathname } = urlObject;

    const pathElements = pathname.split("/");

    // splice the /customize or /collect part of the path
    // pathname could be /organizations/1/properties/1/experiences/1/customize
    // pathname could be /properties/1/experiences/1/customize
    pathElements.splice(isOrganizationsSection ? 7 : 5, 1);

    if (pathElements[1] === "organizations") {
      pathElements.splice(1, 2); // remove organizations and its id, no route for preview on organization
    }

    const openURL = origin + pathElements.join("/");
    window.open(openURL, "_blank");
  };
  const onShare = (isSingleItem: boolean) => {
    dispatch(setSimpleValue("experience_tab_share_trigger", { isSingleItem }));
  };

  return (
    <S.TopTabSection>
      {!isPublicPage(tabStatus) && (
        <>
          <S.ExperienceTabsRoot
            value={adjustedTabStatus}
            TabIndicatorProps={{
              style: {
                backgroundColor: "#25949D",
                bottom: "1px",
                height: "1px",
              },
            }}
            onChange={handleChange}
            aria-label="status tabs"
            variant="standard"
          >
            <S.ExperienceTab
              label={!isBelowSmallWidth && TAB_STATUSES.COLLECT}
              disableRipple
              icon={<S.ExperienceCirclePlusIcon $tabStatus={tabStatus} title={TAB_STATUSES.COLLECT} />}
              iconPosition="start"
            />
            <S.ExperienceTab
              disabled={experienceMediaItemsLength < 1}
              style={{
                opacity: experienceMediaItemsLength < 1 ? ".5" : "1",
              }}
              disableRipple
              label={
                <S.ExperienceTabBadge $tabStatus={tabStatus} badgeContent={experienceMediaItemsLength} color="primary">
                  {!isBelowSmallWidth && TAB_STATUSES.CUSTOMIZE}
                </S.ExperienceTabBadge>
              }
              icon={<S.ExperienceCustomizeIcon $tabStatus={tabStatus} title={TAB_STATUSES.CUSTOMIZE} />}
              iconPosition="start"
              data-testid="experience-tabs-customize"
            />
          </S.ExperienceTabsRoot>
          <S.ExperienceTabBox
            data-testid="experience-tabs-share"
            $isShareDisabled={isShareDisabled}
            onClick={handleShareButtonClick}
          >
            <ShareOptions
              onExperienceShare={onShare}
              label={!isBelowSmallWidth ? TAB_STATUSES.SHARE : undefined}
              fabButtonStyles={customStyles.customFabStyle}
              shareContainerStyles={customStyles.shareContainer}
              shareButtonsContainerStyles={customStyles.shareButtonsContainer}
              customIcon={<S.ExperienceShareIcon $tabStatus={tabStatus} backgroundfill="none" />}
            />
          </S.ExperienceTabBox>
          <S.ExperienceIconButton
            onClick={openPreview}
            style={{
              opacity: experienceMediaItemsLength < 1 ? ".5" : "1",
            }}
            disabled={experienceMediaItemsLength < 1}
            size="large"
            data-testid="experience-tabs-preview"
          >
            <S.ExperiencePreviewIcon $tabStatus={tabStatus} fontSize="large" htmlColor="#fff" />
          </S.ExperienceIconButton>
        </>
      )}
    </S.TopTabSection>
  );
};

export default ExperienceTabs;
